import { PORTAL_ACTIVITIES_QUERY } from "@lib/graphql/queries";
import { useCampaignListChannel } from "@lib/hooks/useCampaignListChannel";
import { Bell } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useQuery } from "urql";

interface PortalActivityData {
  id: string;
  eventType: string;
  metadata: Record<string, unknown> | null;
  campaignTitle: string;
  candidateName: string | null;
  portalUserName: string;
  createdAt: string;
}

const EVENT_LABELS: Record<string, string> = {
  comment: "commented on",
  interview_feedback: "submitted feedback for",
};

function formatActivity(activity: PortalActivityData): string {
  const action = EVENT_LABELS[activity.eventType] ?? activity.eventType;
  const target = activity.candidateName ?? "a candidate";
  return `${activity.portalUserName} ${action} ${target}`;
}

function timeAgo(dateString: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [{ data }, reexecute] = useQuery({ query: PORTAL_ACTIVITIES_QUERY });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(() => {
    reexecute({ requestPolicy: "network-only" });
  }, [reexecute]);

  useCampaignListChannel(refresh);

  const activities: PortalActivityData[] = data?.portalActivities ?? [];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative rounded-md p-1.5 text-text-muted transition-colors hover:bg-card-hover hover:text-text-secondary"
        title="Portal activity"
      >
        <Bell size={18} />
        {activities.length > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">
            {activities.length > 9 ? "9+" : activities.length}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop overlay */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={dropdownRef}
            className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-card shadow-lg"
          >
            <div className="border-b border-border px-4 py-2">
              <h3 className="text-sm font-semibold text-text-primary">Portal Activity</h3>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {activities.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-text-muted">
                  No portal activity yet.
                </p>
              ) : (
                activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="border-b border-border px-4 py-3 last:border-b-0"
                  >
                    <p className="text-sm text-text-secondary">{formatActivity(activity)}</p>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-text-faint">
                      <span>{activity.campaignTitle}</span>
                      <span>&middot;</span>
                      <span>{timeAgo(activity.createdAt)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
